import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Helper hook to manage a delayed state value.
 *
 * @param initialValue - Same as useState's initialValue.
 * @param risingEdgeDelay - The delay to wait before updating the state when the value is rising.
 * @param fallingEdgeDelay - The delay to wait before updating the state when the value is falling.
 *
 * @remarks
 * This hook does not implement useState's behaviour of allowing the setter to be called with a function from the current state.
 * You should always pass the new value directly to the setter.
 */
export function useDelayedBooleanState(
	initialValue: boolean,
	risingEdgeDelay: number,
	fallingEdgeDelay: number,
): [boolean, (newValue: boolean) => void] {
	const [value, setValue] = useState<boolean>(initialValue);

	const risingEdgeTimeout = useRef<number | null>(null);
	const fallingEdgeTimeout = useRef<number | null>(null);

	const updateValue = useCallback(
		(newValue: boolean) => {
			if (newValue === true) {
				// Clear any pending falling edge timeout
				if (fallingEdgeTimeout.current !== null) {
					clearTimeout(fallingEdgeTimeout.current);
					fallingEdgeTimeout.current = null;
				}

				// If there is a rising edge pending already, or the value is already true, do nothing
				if (risingEdgeTimeout.current !== null || value === true) {
					return;
				}

				// If there is no delay, update the value immediately
				if (risingEdgeDelay === 0) {
					setValue(true);
				} else {
					// Otherwise set a new rising edge timeout
					risingEdgeTimeout.current = window.setTimeout(() => {
						risingEdgeTimeout.current = null;
						setValue(true);
					}, risingEdgeDelay);
				}
			} else {
				// Clear any pending rising edge timeout
				if (risingEdgeTimeout.current !== null) {
					clearTimeout(risingEdgeTimeout.current);
					risingEdgeTimeout.current = null;
				}

				// If there is a falling edge pending already, or the value is already false, do nothing
				if (fallingEdgeTimeout.current !== null || value === false) {
					return;
				}

				// If there is no delay, update the value immediately
				if (fallingEdgeDelay === 0) {
					setValue(false);
				} else {
					// Otherwise set a new falling edge timeout
					fallingEdgeTimeout.current = window.setTimeout(() => {
						fallingEdgeTimeout.current = null;
						setValue(false);
					}, fallingEdgeDelay);
				}
			}
		},
		[fallingEdgeDelay, risingEdgeDelay, value],
	);

	return [value, updateValue];
}

/**
 * Helper hook that watches a boolean value and applies delays when it changes.
 * Unlike useDelayedBooleanState, this hook automatically tracks changes to the input value.
 *
 * @param value - The boolean value to track
 * @param risingEdgeDelay - The delay to wait before updating the state when the value changes from false to true
 * @param fallingEdgeDelay - The delay to wait before updating the state when the value changes from true to false
 *
 * @returns The delayed boolean value
 */
export function useDelayedBooleanValue(
	value: boolean,
	risingEdgeDelay: number,
	fallingEdgeDelay: number,
): boolean {
	const [delayedValue, setDelayedValue] = useDelayedBooleanState(
		value,
		risingEdgeDelay,
		fallingEdgeDelay,
	);

	useEffect(() => {
		setDelayedValue(value);
	}, [value, setDelayedValue]);

	return delayedValue;
}
